// stereo-core: great and small circles as projected polylines.
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, DEG, add, scale, perpBasis, unit, cross } from "./vec.js";
import { planeToPole } from "./orient.js";
import { Projection, XY, project, toHemisphere } from "./projection.js";

/** Great circle of a plane: one polyline across the net. */
export function greatCircle(dip: number, dipDir: number, p: Projection, steps = 180): XY[] {
  const pole = planeToPole(dip, dipDir);
  // strike (horizontal, in plane) and dip vector (down-dip, in plane)
  let s: Vec3 = cross(pole, [0, 0, 1]);
  if (Math.hypot(...s) < 1e-9) s = [1, 0, 0]; // horizontal plane
  s = unit(s);
  let d = unit(cross(s, pole));
  if (d[2] > 0) d = scale(d, -1);
  if (p.hemisphere === "upper") d = scale(d, -1);
  const out: XY[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (Math.PI * i) / steps;
    out.push(project(add(scale(s, Math.cos(t)), scale(d, Math.sin(t))), p));
  }
  return out;
}

/** Small circle (cone) of half-angle `angleDeg` about `axis`. May split into
 *  several polylines where it crosses the primitive circle. */
export function smallCircle(axis: Vec3, angleDeg: number, p: Projection, steps = 180): XY[][] {
  const a = unit(axis);
  const [e1, e2] = perpBasis(a);
  const cb = Math.cos(angleDeg * DEG), sb = Math.sin(angleDeg * DEG);
  const parts: XY[][] = [];
  let cur: XY[] = [];
  let prevFlip: boolean | null = null;
  for (let i = 0; i <= steps; i++) {
    const t = (2 * Math.PI * i) / steps;
    const v = add(scale(a, cb), add(scale(e1, sb * Math.cos(t)), scale(e2, sb * Math.sin(t))));
    const [, flip] = toHemisphere(v, p.hemisphere);
    if (prevFlip !== null && flip !== prevFlip) { if (cur.length > 1) parts.push(cur); cur = []; }
    cur.push(project(v, p));
    prevFlip = flip;
  }
  if (cur.length > 1) parts.push(cur);
  // join first and last piece if the loop started mid-arc on the same side
  if (parts.length > 1) {
    const first = parts[0], last = parts[parts.length - 1];
    const f = first[0], l = last[last.length - 1];
    if (Math.hypot(f.x - l.x, f.y - l.y) < 1e-6) { parts[0] = last.concat(first.slice(1)); parts.pop(); }
  }
  return parts;
}
